import React, {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useState,
} from 'react'
import { GoogleReCaptchaContext, IGoogleReCaptchaConsumerProps } from 'react-google-recaptcha-v3'

interface ReCaptchaProviderProps {
  reCaptchaKey?: string
  triggerId?: string
}

const LazyReCaptchaProviderImpl = lazy(() => import('./ReCaptchaProvider'))
const preloadReCaptcha = () => import('./ReCaptchaProvider')

const RecaptchaReadyContext = createContext(false)

const isTriggerEvent = (event: Event, triggerId: string) => {
  const target = event.target
  if (!(target instanceof Element)) return false
  return Boolean(target.closest(`#${triggerId}`))
}

const ReCaptchaProvider: React.FC<React.PropsWithChildren<ReCaptchaProviderProps>> = ({
  reCaptchaKey,
  triggerId,
  children,
}) => {
  const [enabled, setEnabled] = useState(!triggerId)
  const [ready, setReady] = useState(!triggerId)
  const [contextValue, setContextValue] = useState<IGoogleReCaptchaConsumerProps>({})

  useEffect(() => {
    if (enabled || !triggerId) return

    const handleTriggerEvent = (event: Event) => {
      if (!isTriggerEvent(event, triggerId)) return
      preloadReCaptcha().then(() => setEnabled(true))
    }

    document.addEventListener('pointerdown', handleTriggerEvent, { capture: true, passive: true })
    document.addEventListener('focusin', handleTriggerEvent, true)
    document.addEventListener('keydown', handleTriggerEvent, true)
    document.addEventListener('submit', handleTriggerEvent, true)

    return () => {
      document.removeEventListener('pointerdown', handleTriggerEvent, true)
      document.removeEventListener('focusin', handleTriggerEvent, true)
      document.removeEventListener('keydown', handleTriggerEvent, true)
      document.removeEventListener('submit', handleTriggerEvent, true)
    }
  }, [enabled, triggerId])

  return (
    <RecaptchaReadyContext.Provider value={ready}>
      <GoogleReCaptchaContext.Provider value={contextValue}>
        {children}
      </GoogleReCaptchaContext.Provider>

      {enabled && reCaptchaKey && (
        <Suspense fallback={null}>
          <LazyReCaptchaProviderImpl
            reCaptchaKey={reCaptchaKey}
            onReady={(ctx) => {
              setContextValue(ctx)
              setReady(true)
            }}
          />
        </Suspense>
      )}
    </RecaptchaReadyContext.Provider>
  )
}

export default ReCaptchaProvider
